import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useCustomers } from '../context/CustomerContext';
import { formatPhoneNumber } from '../lib/twilio';
import { format } from 'date-fns';
import MessageModal from '../components/MessageModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMessageSquare, FiSearch, FiUsers, FiPlus, FiCheck, FiAlertTriangle, FiClock } = FiIcons;

function Messages() {
  const { state } = useCustomers();
  const { customers, leads } = state;
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all');

  // Combine customers and leads for recipient selection
  const recipients = [
    ...customers.map(customer => ({
      id: customer.id,
      type: 'customer',
      firstName: customer.firstName,
      lastName: customer.lastName,
      phone: customer.phone,
      email: customer.email
    })),
    ...leads.map(lead => ({
      id: lead.id,
      type: 'lead',
      firstName: lead.firstName,
      lastName: lead.lastName,
      phone: lead.phone,
      email: lead.email
    }))
  ];

  // Filter recipients based on search term
  const filteredRecipients = recipients.filter(recipient => {
    const fullName = `${recipient.firstName} ${recipient.lastName}`.toLowerCase();
    const phone = recipient.phone?.toLowerCase() || '';
    const email = recipient.email?.toLowerCase() || '';
    
    return fullName.includes(searchTerm.toLowerCase()) || 
           phone.includes(searchTerm.toLowerCase()) || 
           email.includes(searchTerm.toLowerCase());
  });

  // Load messages from localStorage on component mount
  useEffect(() => {
    const storedMessages = localStorage.getItem('autocrm-messages');
    if (storedMessages) {
      try {
        setMessages(JSON.parse(storedMessages));
      } catch (error) {
        console.error('Error parsing stored messages:', error);
        setMessages([]);
      }
    }
  }, []);

  // Filter messages based on selected filter
  const filteredMessages = messages.filter(message => {
    if (filter === 'all') return true;
    if (filter === 'sent') return message.status === 'sent' || message.status === 'delivered';
    if (filter === 'failed') return message.status === 'failed' || message.status === 'undelivered';
    return true;
  });

  const handleSendMessage = (recipient = null) => {
    setSelectedRecipient(recipient);
    setIsModalOpen(true);
  };

  const handleMessageSent = (messageData) => {
    const updatedMessages = [messageData, ...messages];
    setMessages(updatedMessages);
    
    // Store messages in localStorage
    localStorage.setItem('autocrm-messages', JSON.stringify(updatedMessages));
  };

  const getRecipientName = (phoneNumber) => {
    const recipient = recipients.find(r => r.phone === phoneNumber);
    if (recipient) {
      return `${recipient.firstName} ${recipient.lastName}`;
    }
    return phoneNumber;
  };

  // Stats for message dashboard
  const stats = [
    {
      name: 'Total Messages',
      value: messages.length,
      icon: FiMessageSquare,
      color: 'bg-blue-500',
    },
    {
      name: 'Sent Successfully',
      value: messages.filter(m => m.status === 'sent' || m.status === 'delivered').length,
      icon: FiCheck,
      color: 'bg-green-500',
    },
    {
      name: 'Failed Messages',
      value: messages.filter(m => m.status === 'failed' || m.status === 'undelivered').length,
      icon: FiAlertTriangle,
      color: 'bg-red-500',
    },
    {
      name: 'Contacts with Phone',
      value: recipients.filter(r => r.phone).length,
      icon: FiUsers,
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search recipients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64"
            />
          </div>
          
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Messages</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleSendMessage()}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>New Message</span>
        </motion.button>
      </div>

      {/* Messages and Recipients Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recipients List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recipients</h3>
          </div>
          
          <div className="p-4">
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredRecipients.length > 0 ? (
                filteredRecipients.map((recipient) => (
                  <motion.div
                    key={`${recipient.type}-${recipient.id}`}
                    whileHover={{ backgroundColor: '#f9fafb' }}
                    className="flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => handleSendMessage(recipient)}
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-600 font-medium text-sm">
                          {recipient.firstName.charAt(0).toUpperCase()}
                          {recipient.lastName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-gray-900 text-sm">
                          {recipient.firstName} {recipient.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {recipient.phone || 'No phone number'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        recipient.type === 'customer' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {recipient.type === 'customer' ? 'Customer' : 'Lead'}
                      </span>
                      
                      <SafeIcon 
                        icon={FiMessageSquare} 
                        className="ml-3 w-4 h-4 text-primary-600 hover:text-primary-800" 
                      />
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No recipients found</h3>
                  <p className="text-gray-500">
                    Try adjusting your search or add new customers/leads
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Message History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden lg:col-span-2">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Message History</h3>
          </div>
          
          <div className="p-4">
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {filteredMessages.length > 0 ? (
                filteredMessages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {getRecipientName(message.to)}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {formatPhoneNumber(message.to)}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        message.status === 'sent' || message.status === 'delivered'
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {message.status}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
                      {message.message}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center">
                        <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                        <span>{format(new Date(message.timestamp), 'MMM d, h:mm a')}</span>
                      </div>
                      
                      <div>
                        {message.message.length} characters
                        ({Math.ceil(message.message.length / 160)} {Math.ceil(message.message.length / 160) === 1 ? 'segment' : 'segments'})
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiMessageSquare} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No messages yet</h3>
                  <p className="text-gray-500 mb-6">
                    Start sending messages to your customers and leads
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSendMessage()}
                    className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                  >
                    Send Your First Message
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      <MessageModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRecipient(null);
        }}
        recipient={selectedRecipient}
        onSent={handleMessageSent}
      />
    </div>
  );
}

export default Messages;